import React, { useState, useEffect, useMemo } from 'react';
import { collection, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../firebase/config';
import {
  Search, RefreshCw, ExternalLink, Building2, Calendar,
  Users, AlertCircle, Filter, ChevronDown, ChevronUp, Loader
} from 'lucide-react';
import './GovSupport.css';

const SOURCE_LABELS = {
  bizinfo: '기업마당',
  gov24: '보조금24',
  kstartup: 'K-Startup',
};

const SOURCE_COLORS = {
  bizinfo: '#2563eb',
  gov24: '#059669',
  kstartup: '#7c3aed',
};

function GovSupport({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const businessType = user?.businessType || user?.companyBusinessType || 'general';
  const govType = businessType === 'dental' || businessType === 'clinic' ? 'dental'
    : businessType === 'lab' ? 'lab'
    : 'general';

  // Firestore 캐시에서 로드
  useEffect(() => {
    loadFromCache();
  }, [govType]);

  const loadFromCache = async () => {
    setLoading(true);
    setError(null);
    try {
      const cacheDoc = await getDoc(doc(db, 'govSupport', govType));
      if (cacheDoc.exists()) {
        const data = cacheDoc.data();
        setItems(data.items || []);
        if (data.lastUpdated) {
          setLastUpdated(data.lastUpdated.toDate());
        }
      }
    } catch (e) {
      console.error('캐시 로드 실패:', e);
    }
    setLoading(false);
  };

  // Cloud Function 호출하여 새로고침
  const handleRefresh = async (force = false) => {
    setRefreshing(true);
    setError(null);
    try {
      const functions = getFunctions(getApp(), 'asia-northeast3');
      const fetchGovSupport = httpsCallable(functions, 'fetchGovSupport');
      const result = await fetchGovSupport({ businessType: govType, forceRefresh: force });
      const data = result.data;

      if (data.error) {
        setError(data.error);
      }
      if (data.items) {
        setItems(data.items);
      }
      if (data.lastUpdated) {
        setLastUpdated(new Date(data.lastUpdated));
      } else {
        setLastUpdated(new Date());
      }
    } catch (e) {
      console.error('새로고침 실패:', e);
      setError('데이터를 불러오는데 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
    setRefreshing(false);
  };

  // 필터링된 목록
  const filteredItems = useMemo(() => {
    let filtered = items;
    if (selectedSource !== 'all') {
      filtered = filtered.filter(item => item.source === selectedSource);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        (item.title || '').toLowerCase().includes(term) ||
        (item.description || '').toLowerCase().includes(term) ||
        (item.agency || '').toLowerCase().includes(term) ||
        (item.target || '').toLowerCase().includes(term)
      );
    }
    // 최신순 정렬 (period에서 날짜 파싱, 없으면 뒤로)
    filtered = [...filtered].sort((a, b) => {
      const parseDate = (p) => {
        if (!p) return 0;
        const m = p.match(/(\d{4})[.\-/](\d{1,2})[.\-/](\d{1,2})/);
        return m ? new Date(m[1], m[2] - 1, m[3]).getTime() : 0;
      };
      return parseDate(b.period) - parseDate(a.period);
    });
    return filtered;
  }, [items, selectedSource, searchTerm]);

  // 출처별 개수
  const sourceCounts = useMemo(() => {
    const counts = { all: items.length, bizinfo: 0, gov24: 0, kstartup: 0 };
    items.forEach(item => {
      if (counts[item.source] !== undefined) counts[item.source]++;
    });
    return counts;
  }, [items]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="gov-support-container">
      {/* 헤더 */}
      <div className="gov-support-header">
        <div className="gov-support-header-left">
          <h2 className="gov-support-title">정부지원사업</h2>
          <span className="gov-support-type-badge">
            {govType === 'dental' ? '치과' : govType === 'lab' ? '기공소' : '소상공인'}
          </span>
        </div>
        <div className="gov-support-header-right">
          {lastUpdated && (
            <span className="gov-support-updated">
              마지막 업데이트: {formatDate(lastUpdated)}
            </span>
          )}
          <button
            className="gov-support-refresh-btn"
            onClick={() => handleRefresh(true)}
            disabled={refreshing}
          >
            {refreshing ? <Loader size={16} className="spinning" /> : <RefreshCw size={16} />}
            <span>{refreshing ? '업데이트 중...' : '새로고침'}</span>
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="gov-support-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* 검색 & 필터 */}
      <div className="gov-support-filters">
        <div className="gov-support-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="사업명, 기관명, 지원대상으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="gov-support-source-tabs">
          <button
            className={`gov-source-tab ${selectedSource === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedSource('all')}
          >
            전체 ({sourceCounts.all})
          </button>
          {Object.entries(SOURCE_LABELS).map(([key, label]) => (
            <button
              key={key}
              className={`gov-source-tab ${selectedSource === key ? 'active' : ''}`}
              onClick={() => setSelectedSource(key)}
              style={selectedSource === key ? { backgroundColor: SOURCE_COLORS[key], color: '#fff', borderColor: SOURCE_COLORS[key] } : {}}
            >
              {label} ({sourceCounts[key] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* 목록 */}
      {loading ? (
        <div className="gov-support-loading">
          <Loader size={32} className="spinning" />
          <p>정부지원사업 정보를 불러오는 중...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="gov-support-empty">
          <Building2 size={48} />
          <h3>지원사업 정보가 없습니다</h3>
          {items.length === 0 ? (
            <p>새로고침 버튼을 눌러 최신 정보를 불러와 주세요.</p>
          ) : (
            <p>검색 조건에 맞는 지원사업이 없습니다.</p>
          )}
          {items.length === 0 && (
            <button className="gov-support-refresh-btn primary" onClick={() => handleRefresh(true)} disabled={refreshing}>
              {refreshing ? <Loader size={16} className="spinning" /> : <RefreshCw size={16} />}
              <span>데이터 불러오기</span>
            </button>
          )}
        </div>
      ) : (
        <div className="gov-support-list">
          <p className="gov-support-count">총 {filteredItems.length}건</p>
          {filteredItems.map((item, index) => (
            <div key={item.id || index} className="gov-support-card">
              <div
                className="gov-support-card-header"
                onClick={() => setExpandedId(expandedId === (item.id || index) ? null : (item.id || index))}
              >
                <div className="gov-support-card-title-area">
                  <span
                    className="gov-source-badge"
                    style={{ backgroundColor: SOURCE_COLORS[item.source] || '#64748b' }}
                  >
                    {SOURCE_LABELS[item.source] || item.source}
                  </span>
                  <h3 className="gov-support-card-title">{item.title}</h3>
                </div>
                {expandedId === (item.id || index) ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>

              {/* 기본 정보 */}
              <div className="gov-support-card-meta">
                {item.agency && (
                  <span className="gov-meta-item">
                    <Building2 size={14} /> {item.agency}
                  </span>
                )}
                {item.period && (
                  <span className="gov-meta-item">
                    <Calendar size={14} /> {item.period}
                  </span>
                )}
              </div>

              {/* 확장 영역 */}
              {expandedId === (item.id || index) && (
                <div className="gov-support-card-detail">
                  {item.description && (
                    <div className="gov-detail-section">
                      <strong>사업 내용</strong>
                      <p>{item.description}</p>
                    </div>
                  )}
                  {item.target && (
                    <div className="gov-detail-section">
                      <strong><Users size={14} /> 지원 대상</strong>
                      <p>{item.target}</p>
                    </div>
                  )}
                  {item.url && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="gov-detail-link"
                    >
                      <ExternalLink size={14} /> 상세 페이지 바로가기
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default GovSupport;
